import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import GlobalContextFormFields from "components/GlobalContextFormFields";
import useGlobalContext from "hooks/globalData";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { DirectLink, QrCodeRequest } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { saveAs } from "file-saver";
import GetAppIcon from "@mui/icons-material/GetApp";
import { svgToPng } from "utils/imageUtils";

const QR_CODE_SIZE = 300;
const QR_CODE_DOWNLOAD_SIZE = 600;

type SkuLinkQrCodeDialogProps = {
  open: boolean;
  skuLink?: DirectLink;
  onClose: () => void;
};

type SkuLinkQrCodeDialogFormValues = {
  applicationId?: string;
  name?: string;
  url?: string;
};

const SkuLinkQrCodeDialog = ({
  open,
  skuLink,
  onClose,
}: SkuLinkQrCodeDialogProps) => {
  const { contentService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const context = contextStore.useState();

  const { data: qrCodeResponse, isLoading: qrCodeIsLoading } = useQuery(
    ["qrCode", skuLink?.url],
    () =>
      contentService.generateSvg(QrCodeRequest.fromJS({ data: skuLink?.url })),
    { enabled: !!skuLink }
  );

  const { Form } = withTypes<SkuLinkQrCodeDialogFormValues>();

  const init = {
    ...context,
    name: skuLink?.name,
    url: skuLink?.url,
  } as SkuLinkQrCodeDialogFormValues;

  const downloadPng = async () => {
    if (!qrCodeResponse?.data) {
      return;
    }

    const blob = await svgToPng(
      qrCodeResponse?.data,
      QR_CODE_DOWNLOAD_SIZE,
      QR_CODE_DOWNLOAD_SIZE
    );

    blob && saveAs(blob, `QrCode_${new Date().toJSON()}.png`);
  };

  return (
    <Dialog open={open}>
      <DialogTitle>
        <T id="downloadQrCode" />
      </DialogTitle>
      <Form
        onSubmit={() => {}}
        initialValues={init}
        render={({ values }) => (
          <form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <GlobalContextFormFields values={values} disabled />
                <Field name="name">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "targetPage" })}
                        disabled
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>
                <Field name="url">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "urlLabel" })}
                        disabled
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>
                {qrCodeIsLoading && (
                  <Box textAlign="center" my={2} width="100%">
                    <CircularProgress />
                  </Box>
                )}
                {qrCodeResponse?.data && (
                  <>
                    <Grid item xs={7}>
                      <img
                        height={QR_CODE_SIZE}
                        width={QR_CODE_SIZE}
                        alt=""
                        src={
                          "data:image/svg+xml;utf8," +
                          encodeURIComponent(qrCodeResponse?.data)
                        }
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Box my={15} textAlign="center">
                        <T id="fileTypePng" />
                        <IconButton onClick={downloadPng}>
                          <GetAppIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button data-cy="closeButton" onClick={onClose}>
                {t({ id: "closeButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default SkuLinkQrCodeDialog;
